import { useRef } from "react";
import { motion, useInView, useMotionValue, useSpring } from "framer-motion";
import { useLocation, useNavigate } from "react-router-dom";

export const EASE = [0.16, 1, 0.3, 1];

export const MaskLine = ({ children, delay = 0, className = "", innerClass = "" }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-8%" });
  return (
    <span ref={ref} className={`block overflow-hidden ${className}`}>
      <motion.span
        className={`block will-change-transform ${innerClass}`}
        initial={{ y: "115%" }}
        animate={inView ? { y: "0%" } : { y: "115%" }}
        transition={{ duration: 1.05, delay, ease: EASE }}
      >
        {children}
      </motion.span>
    </span>
  );
};

export const FadeIn = ({ children, delay = 0, y = 28, className = "" }) => (
  <motion.div
    className={className}
    initial={{ opacity: 0, y }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-8%" }}
    transition={{ duration: 0.9, delay, ease: EASE }}
  >
    {children}
  </motion.div>
);

export const Micro = ({ children, className = "" }) => (
  <span
    className={`text-[10px] md:text-xs font-medium tracking-[0.3em] uppercase text-cream/40 ${className}`}
  >
    {children}
  </span>
);

export const Magnetic = ({ children, className = "", strength = 0.3 }) => {
  const ref = useRef(null);
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const sx = useSpring(x, { stiffness: 200, damping: 15 });
  const sy = useSpring(y, { stiffness: 200, damping: 15 });

  const onMove = (e) => {
    if (window.matchMedia("(pointer: coarse)").matches) return;
    const r = ref.current.getBoundingClientRect();
    x.set((e.clientX - (r.left + r.width / 2)) * strength);
    y.set((e.clientY - (r.top + r.height / 2)) * strength);
  };
  const onLeave = () => {
    x.set(0);
    y.set(0);
  };

  return (
    <motion.div
      ref={ref}
      onMouseMove={onMove}
      onMouseLeave={onLeave}
      style={{ x: sx, y: sy }}
      className={`inline-block ${className}`}
    >
      {children}
    </motion.div>
  );
};

export const useHashNav = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  return (hash) => {
    if (pathname !== "/") {
      navigate("/" + hash);
      return;
    }
    const el = document.querySelector(hash);
    if (!el) return;
    if (window.__lenis) window.__lenis.scrollTo(el, { offset: -70 });
    else el.scrollIntoView({ behavior: "smooth" });
  };
};
